import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { LumenApp } from '@lumen/app';
import { HttpBackend, LocalBackend } from '@lumen/data';
import { createWebPlatform } from '@lumen/platform/web';
import { createNativePlatform, nativeBridge } from '@lumen/platform/native';

// Inside the Mac or iOS app the shell provides a bridge and the server's address (ADR 0008).
const bridge = nativeBridge(window);
const platform = bridge ? createNativePlatform(window, bridge) : createWebPlatform(window);
// Production builds work offline after the first visit (P-507).
if (import.meta.env.PROD) platform.installOfflineShell('/sw.js');
// Where the Lumen server lives (ADR 0007):
// - `VITE_LUMEN_SERVER=https://lumen.example.com`: a server on another host (e.g. this app on Vercel);
// - `VITE_LUMEN_BACKEND=local`: no server at all, everything stays in this browser;
// - neither: the server that serves this page.
const server = (bridge?.server ?? (import.meta.env.VITE_LUMEN_SERVER as string | undefined))?.replace(/\/$/, '') || null;
const local = !server && import.meta.env.VITE_LUMEN_BACKEND === 'local';
// Served by the Lumen server itself, the session lives in an httpOnly cookie; across hosts it is a bearer token.
const backend = local ? new LocalBackend(platform.storage) : new HttpBackend(server ?? '', { cookies: !server });
const origin = server ?? `${location.protocol}//${location.host}`;
const syncUrl = local ? null : `${origin.replace(/^http/, 'ws')}/sync`;

// Browser errors go to the server, which logs them and forwards them to Sentry when configured (ADR 0008).
if (!local) {
  let reported = 0;
  const report = (e: { message?: string; name?: string; stack?: string }) => {
    if (!e.message || /ResizeObserver loop/.test(e.message) || reported++ >= 10) return;
    void fetch(`${server ?? ''}/api/client-errors`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ...e, url: location.href, userAgent: navigator.userAgent }),
      keepalive: true,
      credentials: server ? 'omit' : 'same-origin',
    }).catch(() => undefined);
  };
  window.addEventListener('error', (ev) => report({ message: ev.message, name: ev.error?.name, stack: ev.error?.stack }));
  window.addEventListener('unhandledrejection', (ev) => {
    const r = ev.reason;
    report(r instanceof Error ? { message: r.message, name: r.name, stack: r.stack } : { message: String(r), name: 'UnhandledRejection' });
  });
}

// Native app links for the landing page (P-625). A Lumen server's `/api/config` overrides these at
// runtime (LUMEN_MAC_DOWNLOAD_URL / LUMEN_TESTFLIGHT_URL); these two are for builds without a server.
const downloads = {
  mac: (import.meta.env.VITE_LUMEN_MAC_DOWNLOAD_URL as string | undefined)?.trim() || null,
  ios: (import.meta.env.VITE_LUMEN_TESTFLIGHT_URL as string | undefined)?.trim() || null,
};

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <LumenApp platform={platform} backend={backend} syncUrl={syncUrl} document={document} downloads={downloads} />
  </StrictMode>,
);
